import { UserSelectionSetFragment } from "@src/features/messaging/SentMessage.generated";
import { MyTrainingModuleContentMembershipInstanceFragment } from "@src/features/myTraining/courseInstance/contexts/ModuleInstanceContextProvider.generated";
import { ContentMembershipInstanceSection } from "@src/features/myTraining/types";
import { UserSelectionFragment } from "@src/fragments.generated";
import { UserSelectionInput } from "@src/types.generated";
import { listToReadableString } from "@src/lib/utils/strings";

const getRoleString = (userSelection: UserSelectionFragment): string => {
  if (
    userSelection.allTrainees &&
    userSelection.allManagers &&
    userSelection.allAdmins
  ) {
    return "Everyone";
  }
  let parts = [];
  if (userSelection.allTrainees) {
    parts.push("All Trainees");
  }
  if (userSelection.allManagers) {
    parts.push("All Managers");
  }
  if (userSelection.allAdmins) {
    parts.push("All Admins");
  }
  if (userSelection.roleMemberships.length > 0) {
    const roleNames = userSelection.roleMemberships.map((ra) => ra.role.name);
    parts = parts.concat(roleNames);
  }
  if (parts.length === 0) {
    return "Everyone";
  }
  return listToReadableString(parts);
};

const getLocationString = (userSelection: UserSelectionFragment): string => {
  if (userSelection.allLocations) {
    return "All Locations";
  }
  let parts: string[] = [];
  if (userSelection.locationMemberships.length > 0) {
    const locationNames = userSelection.locationMemberships.map(
      (la) => la.location.name,
    );
    parts = parts.concat(locationNames);
  }
  if (userSelection.locationGroupMemberships.length > 0) {
    const locationGroupNames = userSelection.locationGroupMemberships.map(
      (la) => la.locationGroup.name,
    );
    parts = parts.concat(locationGroupNames);
  }
  if (parts.length === 0) {
    return "";
  }
  return listToReadableString(parts);
};

export const getAutomationString = (
  userSelection: UserSelectionFragment,
): string => {
  const roleString = getRoleString(userSelection);
  const locationString = getLocationString(userSelection);
  if (locationString) {
    return `${roleString} in ${locationString}`;
  }
  return roleString;
};

export const isUserSelectionEmpty = (
  userSelection: UserSelectionFragment,
): boolean => {
  return (
    userSelection.allTrainees == false &&
    userSelection.allManagers == false &&
    userSelection.allAdmins == false &&
    userSelection.allLocations == false &&
    !userSelection.everyoneAtOrgOrBrand &&
    userSelection.locationMemberships.length === 0 &&
    userSelection.roleMemberships.length === 0 &&
    userSelection.locationGroupMemberships.length === 0 &&
    userSelection.roleGroupMemberships.length === 0 &&
    userSelection.userMemberships.length === 0
  );
};

export const userSelectionToUserSelectionInput = (
  userSelection: UserSelectionFragment,
): UserSelectionInput => {
  const input: UserSelectionInput = { ...userSelection };
  input.locationGroupIds = userSelection.locationGroupMemberships.map(
    (x) => x.locationGroup.id,
  );
  input.roleIds = userSelection.roleMemberships.map((x) => x.role.id);
  input.locationIds = userSelection.locationMemberships.map(
    (x) => x.location.id,
  );
  input.roleGroupIds = userSelection.roleGroupMemberships.map(
    (x) => x.roleGroup.id,
  );
  input.userIds = userSelection.userMemberships.map((x) => x.user.id);

  return input;
};

export const isUserSelectionInputEmpty = (
  userSelection: UserSelectionInput,
): boolean => {
  return (
    userSelection.allTrainees == false &&
    userSelection.allManagers == false &&
    userSelection.allAdmins == false &&
    userSelection.allLocations == false &&
    !userSelection.everyoneAtOrgOrBrand &&
    !userSelection.userIds?.length &&
    (!userSelection.locationIds || userSelection.locationIds.length === 0) &&
    (!userSelection.roleIds || userSelection.roleIds.length === 0) &&
    (!userSelection.locationGroupIds ||
      userSelection.locationGroupIds.length === 0) &&
    (!userSelection.roleGroupIds || userSelection.roleGroupIds.length === 0)
  );
};

/**
 * True when the selection filters on locations or location groups but names no
 * audience (user types, roles, departments, everyone, or specific people). The
 * backend matches `users | (locations & roles)`, so this shape matches no one.
 */
export const isUserSelectionInputLocationOnly = (
  userSelection: UserSelectionInput,
): boolean => {
  const hasLocationFilters =
    !!userSelection.locationIds?.length ||
    !!userSelection.locationGroupIds?.length ||
    userSelection.allLocations === true;
  return (
    hasLocationFilters &&
    !userSelection.allTrainees &&
    !userSelection.allManagers &&
    !userSelection.allAdmins &&
    !userSelection.everyoneAtOrgOrBrand &&
    !userSelection.roleIds?.length &&
    !userSelection.roleGroupIds?.length &&
    !userSelection.userIds?.length
  );
};

// Copying getSectionGroups from mobile
export const getModuleContentMembershipInstanceSections = (
  contentMembership: MyTrainingModuleContentMembershipInstanceFragment[],
): ContentMembershipInstanceSection[] => {
  // TODO: Translate "sectionBreak.title.en";
  let section: ContentMembershipInstanceSection = { title: "", contents: [] };
  let sections: ContentMembershipInstanceSection[] = [];

  contentMembership.forEach((cm) => {
    if (cm.sectionBreakInstance) {
      if (section.contents.length > 0) {
        sections = [...sections, section];
      }
      section = {
        title: cm.sectionBreakInstance.sectionBreak.title.myTranslation,
        contents: [],
      };
    } else {
      section.contents.push(cm);
    }
  });
  if (section.contents.length > 0) {
    sections = [...sections, section];
  }

  return sections;
};

export function getMostRecentDate(x: string[]): string | null {
  if (x.length === 1) return x[0];
  return (
    x.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null
  );
}

export const isUserSelectionSetEmpty = (
  userSelectionSet?: UserSelectionSetFragment | null,
): boolean => {
  if (!userSelectionSet) return true;
  if (userSelectionSet.userSelections.length === 0) return true;

  return userSelectionSet.userSelections.every(isUserSelectionEmpty);
};
